import { createFileRoute } from "@tanstack/react-router";
import { LocationPageTemplate, type LocationVariant } from "@/components/LocationPageTemplate";
import { getLocationCity } from "@/data/location-cities";
import { canonical } from "@/lib/seo";

const PATH = "/tech-support-montclair-ca";
const TITLE = "Tech Support Montclair CA | SimpleTech – (909) 255-9112";
const DESCRIPTION =
  "Need tech support in Montclair, CA? SimpleTech Helper offers fast, local, jargon-free IT help for homes & small businesses. Call (909) 255-9112.";

export const Route = createFileRoute("/tech-support-montclair-ca")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      {
        name: "keywords",
        content:
          "tech support Montclair CA, computer help Montclair, IT support Montclair, local tech support Inland Empire, (909) 255-9112",
      },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonical(PATH) },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "geo.region", content: "US-CA" },
      { name: "geo.placename", content: "Montclair" },
      { name: "geo.position", content: "34.0775;-117.6897" },
      { name: "ICBM", content: "34.0775, -117.6897" },
    ],
    links: [{ rel: "canonical", href: canonical(PATH) }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "SimpleTech Helper",
          address: {
            "@type": "PostalAddress",
            streetAddress: "8880 N Benson Ave #121",
            addressLocality: "Montclair",
            addressRegion: "CA",
            postalCode: "91763",
            addressCountry: "US",
          },
          telephone: "(909) 255-9112",
          url: "https://simpletechhelper.com",
          areaServed: "Montclair CA, Inland Empire",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: TechSupportMontclairPage,
});

const FAQS = [
  {
    q: "Do you offer same-day tech support in Montclair?",
    a: "Yes — most Montclair appointments are booked same-day during business hours, and on-site visits typically arrive within 45 minutes. Call (909) 255-9112 and we'll get someone moving right away.",
  },
  {
    q: "How much does tech support cost in Montclair, CA?",
    a: "Most home and small-business jobs are billed by the visit at a flat local rate. We'll quote the work before we start — no surprise charges, no hidden fees, and no jargon.",
  },
  {
    q: "What kinds of tech issues do you fix?",
    a: "Slow computers, Wi-Fi and internet problems, printer setup, email and Microsoft 365 issues, virus & malware removal, data backup and recovery, smart-home setup, and patient one-on-one training. If it has a screen or a plug, we can probably help.",
  },
  {
    q: "Do you help seniors with tech in Montclair?",
    a: "Absolutely. A huge part of what we do is patient, no-jargon tech help for seniors in Montclair and the Inland Empire — phones, tablets, video calls, photos, and printers explained at a comfortable pace.",
  },
  {
    q: "Do you support small businesses too?",
    a: "Yes — from solo realtors to 25-person offices. We handle break/fix, network setup, cybersecurity, and ongoing managed-IT plans for Montclair small businesses.",
  },
];

const INTRO = (
  <>
    <p>
      Computer slow? Wi-Fi dropping? Printer refusing to talk to your laptop?{" "}
      <strong className="text-[#0b0a3d]">SimpleTech Helper</strong> is your local Montclair, CA
      tech support team — fast, friendly, and totally jargon-free. We help homes and small
      businesses across Montclair and the Inland Empire with the kind of patient, hands-on help
      a national call center will never give you.
    </p>
    <p>
      Based right here at{" "}
      <strong className="text-[#0b0a3d]">8880 N Benson Ave #121, Montclair, CA 91763</strong>, we
      serve Montclair, Upland, Claremont, Ontario, Rancho Cucamonga, and the surrounding
      communities. One call, one local technician, one fixed problem — usually the same day.
    </p>
    <p>
      Need help right now? Call{" "}
      <a href="tel:+19092559112" className="text-sky-600 font-bold hover:underline whitespace-pre-line">
        {"\n"}(909) 255-9112
      </a>
      .
    </p>
  </>
);

const VARIANT: LocationVariant = {
  serviceLabel: "Tech Support",
  serviceLabelShort: "Tech Support",
  parentHref: "/services/tech-support",
  parentLabel: "Tech Support",
  learnMoreText: "EXPLORE OUR TECH SUPPORT SERVICES",
  nearMeEyebrow: "Tech Support Near Me",
  whyTitle: "Why Montclair Chooses SimpleTech for Tech Support",
  expertHeroTitle: "Tech Support in Montclair, CA — Fast, Local, and Jargon-Free",
  expertNearMeTitle: "Trusted Tech Support in Montclair, CA",
  introStrong: "tech support in Montclair, CA",
  introBody: INTRO,
  faqHeading: (
    <>Frequently Asked<br />Questions About<br />Tech Support in Montclair</>
  ),
  ctaTitle: "Limited-Time Offer: Free 15-Minute Tech Consultation!",
  blogEyebrow: "From the Blog",
  blogTitle: "Tech Tips for Montclair Homes & Businesses",
  faqs: FAQS,
};

function TechSupportMontclairPage() {
  const city = getLocationCity("montclair")!;
  return <LocationPageTemplate city={city} variant={VARIANT} />;
}
